import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Button, InputElement, Label, Loader } from "components";
import {
  useBackgroundImages,
  useCreateBackgroundImage,
  useDeleteBackgroundImage,
  useUpdateBackgroundColor,
  useUpdateBackgroundActivation,
} from "hooks";
import { showErrorMessage, showSuccessMessage } from "utils";
import classnames from "classnames";

const DEFAULT_COLOR = "#1F2937";

const normalizeColorValue = (value: string) => {
  if (!value) {
    return "#";
  }
  let normalized = value.trim();
  if (!normalized.startsWith("#")) {
    normalized = `#${normalized}`;
  }
  if (normalized.length > 7) {
    normalized = normalized.slice(0, 7);
  }
  return normalized.toUpperCase();
};

export function VisualThemeSection() {
  const [color, setColor] = useState<string>(DEFAULT_COLOR);
  const [variant, setVariant] = useState<"desktop" | "mobile">("desktop");
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(() => Date.now());
  const [localError, setLocalError] = useState<string | null>(null);
  const [editingColors, setEditingColors] = useState<Record<string, string>>({});

  const {
    data: backgroundResponse,
    isLoading: isFetching,
  } = useBackgroundImages({ refetchOnWindowFocus: false });

  const backgrounds = useMemo(() => backgroundResponse?.data ?? [], [backgroundResponse]);

  const {
    mutateAsync: uploadBackground,
    isLoading: isUploading,
  } = useCreateBackgroundImage();

  const {
    mutateAsync: removeBackground,
    isLoading: isDeleting,
  } = useDeleteBackgroundImage();

  const {
    mutateAsync: updateBackground,
    isLoading: isUpdating,
  } = useUpdateBackgroundColor();

  const {
    mutateAsync: updateActivation,
    isLoading: isActivating,
  } = useUpdateBackgroundActivation();

  const [deletingName, setDeletingName] = useState<string | null>(null);
  const [updatingName, setUpdatingName] = useState<string | null>(null);
  const [togglingName, setTogglingName] = useState<string | null>(null);

  useEffect(() => {
    setEditingColors((prev) => {
      const next = { ...prev };

      backgrounds.forEach((item) => {
        if (item.name && !next[item.name]) {
          next[item.name] = normalizeColorValue(item.color || DEFAULT_COLOR);
        }
      });

      Object.keys(next).forEach((key) => {
        if (!backgrounds.find((item) => item.name === key)) {
          delete next[key];
        }
      });

      return next;
    });
  }, [backgrounds]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError(null);

    if (!file) {
      setLocalError("Select an image before uploading.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("color", color);
    formData.append("type", variant);

    try {
      await uploadBackground(formData);
      showSuccessMessage("Background uploaded successfully");
      setFile(null);
      setFileInputKey(Date.now());
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to upload background. Try again later.";
      showErrorMessage(message);
    }
  };

  const handleColorInputChange = (name: string, value: string) => {
    setEditingColors((prev) => ({
      ...prev,
      [name]: normalizeColorValue(value),
    }));
  };

  const handleUpdateColor = async (name: string) => {
    setLocalError(null);
    const selected = editingColors[name];
    if (!selected) {
      setLocalError("Select a colour before saving.");
      return;
    }

    setUpdatingName(name);
    try {
      await updateBackground({ name, color: selected });
      showSuccessMessage("Background colour updated");
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to update background colour.";
      showErrorMessage(message);
    } finally {
      setUpdatingName(null);
    }
  };

  const handleDelete = async (name: string) => {
    setDeletingName(name);
    try {
      await removeBackground(name);
      showSuccessMessage("Background removed");
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to delete background.";
      showErrorMessage(message);
    } finally {
      setDeletingName(null);
    }
  };

  const handleToggleActivation = async (name: string, active: boolean) => {
    setTogglingName(name);
    try {
      await updateActivation({ name, active });
      const message = active
        ? "Background added to rotation"
        : "Background removed from rotation";
      showSuccessMessage(message);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to update rotation preference.";
      showErrorMessage(message);
    } finally {
      setTogglingName(null);
    }
  };

  return (
    <section className="flex flex-col gap-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900">Visual Theme</h2>
        <p className="mt-3 text-sm text-gray-600">
          Upload a hero background image and pair it with the colour that should tint the
          application shell. Use desktop assets for large screens and mobile variants for a more
          focused crop.
        </p>
        <form
          className="mt-6 grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label
                name="Image"
                className="text-sm font-semibold text-gray-800"
              />
              <input
                key={fileInputKey}
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const nextFile = event.target.files?.[0] ?? null;
                  setFile(nextFile);
                }}
                className="block w-full cursor-pointer rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <p className="text-xs text-gray-500">
                Recommended ratio 16:9. Existing uploads are replaced when you provide the same
                name.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Label
                name="Theme colour"
                className="text-sm font-semibold text-gray-800"
              />
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={color}
                  onChange={(event) => setColor(normalizeColorValue(event.target.value))}
                  className="h-12 w-16 cursor-pointer rounded-md border border-gray-200"
                />
                <InputElement
                  value={color}
                  onChange={(event) => {
                    setColor(normalizeColorValue(event.target.value));
                  }}
                  className="text-sm"
                  placeholder="#1F2937"
                />
              </div>
              <p className="text-xs text-gray-500">
                Use standard hex values. The colour previews behind navigation and other shells.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Label
                name="Optimised for"
                className="text-sm font-semibold text-gray-800"
              />
              <div className="flex gap-3">
                {(["desktop", "mobile"] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setVariant(value)}
                    className={classnames(
                      "rounded-md border px-4 py-2 text-sm font-semibold transition",
                      {
                        "border-indigo-500 bg-indigo-50 text-indigo-700": variant === value,
                        "border-gray-200 bg-gray-50 text-gray-600": variant !== value,
                      }
                    )}
                  >
                    {value === "desktop" ? "Desktop" : "Mobile"}
                  </button>
                ))}
              </div>
            </div>
            {localError && <p className="text-sm text-red-500">{localError}</p>}
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                disabled={isUploading}
                isStyleless
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
              >
                {isUploading ? "Uploading…" : "Upload background"}
              </Button>
              {isUploading && <Loader type="rings" />}
            </div>
          </div>
          <div className="flex flex-col gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5">
            <span className="text-sm font-semibold text-gray-700">Live preview</span>
            <div
              className="flex h-40 w-full items-end justify-between rounded-md border border-gray-200 p-4 text-xs text-white shadow-inner"
              style={{ background: color || DEFAULT_COLOR }}
            >
              <span>Navigation</span>
              <span>Footer</span>
            </div>
            <p className="text-xs text-gray-500">
              The preview approximates how the colour wraps around application chrome. Background
              images rotate automatically across eligible pages.
            </p>
          </div>
        </form>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Existing backgrounds</h3>
          {(isFetching || isDeleting) && <Loader type="rings" />}
        </div>
        {!isFetching && backgrounds.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
            No backgrounds uploaded yet. Add one above to see it here.
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {backgrounds.map((background) => (
            <article
              key={background.name || background.url}
              className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              {background.name && (
                <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span>
                    {(background.active ?? true)
                      ? "Included in rotation"
                      : "Excluded from rotation"}
                  </span>
                  <Button
                    isStyleless
                    onClick={() =>
                      handleToggleActivation(
                        background.name!,
                        !(background.active ?? true)
                      )
                    }
                    disabled={togglingName === background.name || isActivating}
                    className={classnames(
                      "rounded-md border px-2 py-1 text-xs font-semibold transition",
                      {
                        "border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100":
                          !(background.active ?? true),
                        "border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100":
                          background.active ?? true,
                        "cursor-not-allowed opacity-60":
                          togglingName === background.name || isActivating,
                      }
                    )}
                  >
                    {(background.active ?? true) ? "Exclude" : "Include"}
                  </Button>
                </div>
              )}
              <div className="aspect-video overflow-hidden rounded-md border border-gray-200 bg-gray-100">
                {background.url ? (
                  <img
                    src={background.url}
                    alt={`${background.type || "background"} preview`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-gray-500">
                    Preview unavailable
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1 text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">
                    {(background.type || "desktop").toUpperCase()}
                  </span>
                  <span>{background.color || "#FFFFFF"}</span>
                  <span className="text-xs text-gray-400 break-all">
                    {background.name || "unnamed"}
                  </span>
                </div>
                <span
                  className="h-10 w-10 rounded-full border border-gray-300"
                  style={{
                    backgroundColor:
                      (background.name && editingColors[background.name]) ||
                      background.color ||
                      "#FFFFFF",
                  }}
                />
              </div>
              {background.name && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={editingColors[background.name] || DEFAULT_COLOR}
                      onChange={(event) =>
                        handleColorInputChange(background.name!, event.target.value)
                      }
                      className="h-10 w-12 cursor-pointer rounded-md border border-gray-200"
                    />
                    <InputElement
                      value={editingColors[background.name] || DEFAULT_COLOR}
                      onChange={(event) =>
                        handleColorInputChange(background.name!, event.target.value)
                      }
                      className="text-sm"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      isStyleless
                      disabled={
                        isUpdating ||
                        updatingName === background.name ||
                        !editingColors[background.name]
                      }
                      onClick={() => handleUpdateColor(background.name!)}
                      className="rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
                    >
                      {updatingName === background.name ? "Saving…" : "Save colour"}
                    </Button>
                    <Button
                      isStyleless
                      disabled={isDeleting || deletingName === background.name}
                      onClick={() => handleDelete(background.name!)}
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                    >
                      {deletingName === background.name ? "Deleting…" : "Delete"}
                    </Button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
